import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, ShieldCheck, Users, Package, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from './AuthProvider';
import './styles/tw.css';

/**
 * GİRİŞ EKRANI — 2026-08-29 yeniden tasarlandı.
 *
 * Önceki hâli tam ekran bir dağ fotoğrafı üzerine buzlu cam karttı. Ürün bir
 * teknik servis/cari programı; stok fotoğrafı ne işi anlatıyordu ne de yeni
 * ana sayfayla aynı dili konuşuyordu. Şimdi ana sayfanın düzeniyle aynı:
 * beyaz zemin, mavi vurgu, solda ne satın aldığını hatırlatan marka paneli.
 *
 * ⚠️ Giriş mantığına DOKUNULMADI — alanlar, hata yönetimi, abonelik süresi
 * dolmuş kontrolü ve yönlendirmeler birebir aynı.
 * `SignIn.css` artık kullanılmıyor (Tailwind'e geçildi); dosya duruyor ama
 * içe aktarılmıyor.
 */

const ARTILAR = [
  { ikon: Wrench, metin: 'Cihaz kabulünden teslimata tüm servis akışı' },
  { ikon: Users, metin: 'Cari hesap, borç/alacak ve tahsilat takibi' },
  { ikon: Package, metin: 'Stok, kasa ve fatura tek veritabanında' },
];

/**
 * Girişten sonra gidilecek adres. WhatsApp'tan gelen saha servisi bağlantısı
 * gibi derin bağlantılar giriş ekranına düşünce kaybolmasın diye
 * `App.tsx > HedefiHatirla` tarafından saklanır.
 * Tek kullanımlık: okunduğu anda silinir, sonraki girişlerde tekrar kullanılmaz.
 */
const girisSonrasiHedef = (): string => {
    try {
        const h = sessionStorage.getItem('girisSonrasiHedef');
        sessionStorage.removeItem('girisSonrasiHedef');
        // Yalnız uygulama içi yol kabul edilir (açık yönlendirme koruması).
        if (h && h.startsWith('/') && !h.startsWith('//')) return h;
    } catch { /* özel sekme */ }
    return '/dashboard';
};

const SignIn: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user, login } = useAuth();

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate(girisSonrasiHedef(), { replace: true });
    }
  }, [user, navigate]);

  const handleEmailSignIn = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      // Success - useAuth will handle navigation
    } catch (err: any) {
      const errorData = err?.response?.data;

      // Check if subscription expired
      if (errorData?.error === 'subscription_expired') {
        const expiresAt = errorData?.data?.expiresAt
          ? new Date(errorData.data.expiresAt).toLocaleDateString('tr-TR')
          : '';
        setError(
          `Aboneliğinizin süresi ${expiresAt ? expiresAt + ' tarihinde' : ''} dolmuştur.\n` +
          `Sisteme erişim sağlamak için lütfen aboneliğinizi yenileyiniz.\n` +
          `Abonelik bilgileri için: info@tservis.tr`
        );
      } else {
        setError(errorData?.message || err?.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const girdiSinifi =
    'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 ' +
    'shadow-sm outline-none transition-colors placeholder:text-slate-400 ' +
    'focus:border-vurgu-500 focus:ring-2 focus:ring-vurgu-500/20 disabled:bg-slate-50 disabled:text-slate-500';

  return (
    <div className="tw-alan min-h-screen bg-white font-sans text-slate-700 antialiased [&_*]:box-border">
      <div className="grid min-h-screen lg:grid-cols-2">

        {/* ══════════ SOL: MARKA PANELİ (yalnız geniş ekran) ══════════ */}
        <div className="relative hidden overflow-hidden bg-gradient-to-br from-vurgu-600 to-indigo-700 p-12 lg:flex lg:flex-col lg:justify-between">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />

          <a href="/" className="relative flex items-center gap-2.5 no-underline">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-white/15 ring-1 ring-white/25">
              <Wrench className="h-[18px] w-[18px] text-white" />
            </span>
            <span className="text-[17px] font-semibold text-white">tservis</span>
          </a>

          <div className="relative">
            <h2 className="max-w-md text-[2.1rem] font-bold leading-[1.15] tracking-tight text-white">
              İşletmenizin tamamı, tek panelde.
            </h2>
            <ul className="mt-8 flex flex-col gap-4">
              {ARTILAR.map(({ ikon: I, metin }) => (
                <li key={metin} className="flex items-center gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/15">
                    <I className="h-4 w-4 text-white" />
                  </span>
                  <span className="text-[15px] text-blue-50">{metin}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="relative flex items-center gap-2 text-[13px] text-blue-200">
            <ShieldCheck className="h-4 w-4" />
            Verileriniz SSL ile şifrelenir, her gece yedeklenir.
          </p>
        </div>

        {/* ══════════ SAĞ: FORM ══════════ */}
        <div className="flex items-center justify-center px-5 py-12 sm:px-10">
          <div className="w-full max-w-[380px]">

            {/* Dar ekranda sol panel gizli; marka burada görünür */}
            <a href="/" className="mb-10 flex items-center gap-2.5 no-underline lg:hidden">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-vurgu-500/10 ring-1 ring-vurgu-500/20">
                <Wrench className="h-[18px] w-[18px] text-vurgu-600" />
              </span>
              <span className="text-[17px] font-semibold text-slate-900">tservis</span>
            </a>

            <h1 className="text-[1.6rem] font-bold tracking-tight text-slate-900">Hesabınıza giriş yapın</h1>
            <p className="mt-2 text-[15px] text-slate-600">E-posta ve şifrenizle devam edin.</p>

            {error && (
              <div
                role="alert"
                className="mt-6 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-[13.5px] leading-relaxed text-red-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <span className="whitespace-pre-line">{error}</span>
              </div>
            )}

            <form className="mt-7 flex flex-col gap-5" onSubmit={handleEmailSignIn}>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  E-posta
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={girdiSinifi}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="ornek@email.com"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <label htmlFor="password" className="block text-[13px] font-semibold text-slate-700">
                    Şifre
                  </label>
                  {/* Şifre sıfırlama akışı henüz yok; kullanıcıyı boş bir sayfaya
                      göndermek yerine ne yapması gerektiğini söylüyoruz. */}
                  <button
                    type="button"
                    className="text-[13px] font-medium text-vurgu-600 hover:underline"
                    onClick={() =>
                      setError('Şifrenizi sıfırlamak için info@tservis.tr adresine yazın ya da yöneticinizle iletişime geçin.')
                    }
                  >
                    Şifremi unuttum
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className={girdiSinifi}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-vurgu-500 px-5 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-vurgu-600/25 transition-colors hover:bg-vurgu-600 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Giriş yapılıyor…
                  </>
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>

            <p className="mt-8 border-t border-slate-200 pt-6 text-center text-[14px] text-slate-600">
              Hesabınız yok mu?{' '}
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center gap-1 font-semibold text-vurgu-600 hover:underline"
              >
                14 gün ücretsiz deneyin <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
